import { useT } from '@/session/useSession'
import { Badge } from '../ui/badge'
import styles from './StatusBadge.module.css'

type Tone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'outline'
  | 'purple'
  | 'teal'
  | 'pink'

const TONES: Record<string, Tone> = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
  internal: 'neutral',
  public: 'success',
  renewed: 'neutral',
  insert: 'success',
  update: 'info',
  delete: 'danger',
  created: 'outline',
  retry: 'warning',
  completed: 'success',
  failed: 'danger',
  contacted: 'warning',
  unqualified: 'outline',
  won: 'success',
  lost: 'danger',
  enabled: 'success',
  disabled: 'danger',
  open: 'outline',
  in_progress: 'info',
  draft: 'outline',
  issued: 'warning',
  paid: 'success',
  subscriber: 'outline',
  lead: 'outline',
  opportunity: 'info',
  customer: 'success',
}

const SCOPED: Record<string, Record<string, Tone>> = {
  'crm.jobs.state': {
    active: 'info',
    cancelled: 'outline',
  },
  'crm.campaigns.creation_status': {
    DRAFT: 'outline',
    DONE: 'success',
  },
  'crm.webhook_subscriptions.delivery_state': {
    PENDING: 'warning',
    DELIVERED: 'success',
    FAILED: 'danger',
    ABANDONED: 'neutral',
  },
}

const DOTTED: ReadonlySet<Tone> = new Set([
  'success',
  'warning',
  'danger',
  'info',
  'purple',
  'teal',
  'pink',
])

export function StatusBadge({ status, scope }: { status: string; scope: string }) {
  const t = useT()
  const tone = SCOPED[scope]?.[status] ?? TONES[status] ?? 'outline'

  return (
    <Badge
      variant={tone === 'outline' ? 'outline' : 'secondary'}
      className={styles.badge}
      data-tone={tone}
    >
      {DOTTED.has(tone) ? <span className={styles.dot} aria-hidden="true" /> : null}
      {t(`${scope}.${status}`)}
    </Badge>
  )
}
